import { IModel, IUser } from '@alberta/konexi-shared';
import { NgZone, inject } from '@angular/core';
import { Paginated, Params, Query, Service } from '@feathersjs/feathers';
import { TrackerService } from '@services/tracker.service';
import { cloneDeep, upperFirst } from 'lodash';
import { SyncTimestampWorkItem } from 'src/app/business/timestamp/sync-timestamp-work-item';
import { ConnectionMode } from 'src/app/common/contracts/connection/connection-mode';
import { IAppController } from 'src/app/common/contracts/controller/app-controller';
import { IDispatcher } from 'src/app/common/contracts/dispatch/dispatcher';
import { LogLevel } from 'src/app/common/contracts/logging/log-level';
import { IPlatformSync } from 'src/app/common/contracts/sync/platform-sync';
import { ISyncState } from 'src/app/common/contracts/sync/sync-state';
import { IWorkItem } from 'src/app/common/contracts/work-item/work-item_T';
import { paramsForServer } from 'src/app/common/feathers/params-for-server';
import { ObservableModel } from 'src/app/common/viewmodel/observable-model';
import { ICancellationToken } from '../../../common/contracts/cancellation/cancellation-token';
import { MaintenanceModelName, PatientAppUserModelName, UsersModelName } from '../../models/model-names';
import { SyncProgressEvent } from '../../models/sync-progress-event';
import { IAdditionalSyncData } from '../contracts/sync/additional-sync-data';
import { IDatabaseSynchronizer } from '../contracts/sync/database-synchronizer_T';
import { IFeathersAppProvider } from '../contracts/sync/feathers-app-provider';
import { IFullSyncService } from '../contracts/sync/service/full-sync-service';
import { ISyncService } from '../contracts/sync/service/sync-service';
import { EventService } from '../event.service';

export abstract class SyncService<T extends IModel> implements ISyncService, IFullSyncService {
  public get name(): string {
    return this._name;
  }

  protected _service: Service<any>;
  private _syncTimestampWorkItem: SyncTimestampWorkItem;
  protected _workItem: IWorkItem<T> | IDatabaseSynchronizer<T>;
  protected limit = 2000;
  protected ignoreSync = false;
  protected ignoreState: boolean;
  protected _trackerService: TrackerService;

  constructor(
    protected _name: string,
    protected _appController: IAppController,
    protected _dispatcher: IDispatcher<T>,
    protected _eventService: EventService<SyncProgressEvent>,
    private _ngZone: NgZone,
    protected platformSync: IPlatformSync
  ) {
    // using inject() instead of constructor to avoid forcing all extenders
    // of SyncService to also inject via constructor
    // because they need to call super() when subclassing
    this._trackerService = inject(TrackerService);
    if (!this._name) {
      throw new Error('Name of service cannot be empty or null/undefined.');
    }

    this._syncTimestampWorkItem = this._appController.getWorkItemByCtor(
      'SyncTimestampWorkItem'
    ) as SyncTimestampWorkItem;

    this._workItem = this._appController.getWorkItemByCtor(`${upperFirst(this._name)}WorkItem`);
  }

  public resolve(): ISyncService[] {
    return [this];
  }

  public abstract canSync(channel: string): boolean;

  protected patchParams(params: Params): Params {
    return params;
  }

  protected afterSync(syncState: ISyncState<T>): Promise<void> {
    return;
  }

  protected async afterPatch(entity: T): Promise<void> {
    return;
  }

  public async write(payload: IModel, additionalData: IAdditionalSyncData, channel: string, user): Promise<T> {
    if (!payload) {
      throw new Error('Payload to sync must be a valid object of type IModel');
    }

    const method = channel.substring(channel.lastIndexOf(':') + 1);

    switch (method) {
      case 'create': {
        const params = this.getParamsWithAuth(user);
        this._trackerService.debugSyncServiceCreate(this._name, payload?._id);
        return this._service.create(payload, params);
      }
      case 'delete': {
        this._trackerService.debugSyncServiceRemove(this._name, payload?._id);
        return this._service.remove(payload._id, this.getParamsWithAuth(user));
      }
      case 'get': {
        this._trackerService.debugSyncServiceGet(this._name, payload?._id);
        return this._service.get(payload._id, this.getParamsWithAuth(user));
      }
      case 'update': {
        this._trackerService.debugSyncServiceUpdate(this._name, payload?._id);
        const params = this.getParamsWithAuth(user);
        return this._service.update(payload._id, payload, params);
      }
      case 'patch': {
        try {
          const params: Params = { query: {} };
          if (additionalData.changes) {
            const { query } = params;
            paramsForServer({
              query,
              syncData: this.createSyncData(user, additionalData),
            });
          }
          this._trackerService.debugSyncServicePatch(this._name, payload?._id);
          const result = await this._service.patch(payload._id, payload, params);
          await this.afterPatch(result);

          return result;
        } catch (error) {
          if (error && error.code && error.code === 403) {
            return null;
          }
          throw error;
        }
      }
    }
  }

  public async setup(feathersAppProvider: IFeathersAppProvider): Promise<void> {
    await feathersAppProvider.ready;

    if (!feathersAppProvider.app) {
      throw new Error('Feathers application must be provided for synchronisation.');
    }

    this._service = feathersAppProvider.app.service(this._name);

    this.removeAllListeners();

    this._service.on('created', item => this.onCreated(item));
    this._service.on('updated', item => this.onPatched(item));
    this._service.on('patched', item => this.onPatched(item));
    this._service.on('removed', item => this.onRemoved(item));
  }

  public removeAllListeners(connectionMode?: ConnectionMode): void {
    if (connectionMode && connectionMode === ConnectionMode.maintenance && this._name === MaintenanceModelName) {
      return;
    }

    this._service.removeAllListeners('created');
    this._service.removeAllListeners('updated');
    this._service.removeAllListeners('patched');
    this._service.removeAllListeners('removed');
  }

  protected async syncInternal(
    token: ICancellationToken,
    user: any,
    timestamp: Date,
    initialSyncStart: Date,
    resetServiceRepeats: (token: ICancellationToken, serviceName: string) => void,
    resetSyncTimestamp: (data) => Promise<void>
  ): Promise<any> {
    if (!this._service) {
      throw new Error(
        `Service with name ${this._name} could not be found. Please specify a correct name for your synchronisation
        service or take care your service instance is running.`
      );
    }

    if (this.ignoreSync) {
      return;
    }

    token.syncCompleted[this._name] = false;

    this._trackerService.debugSyncServiceSyncInternalStart(this._name, timestamp, initialSyncStart);

    try {
      let query: Query = {
        $limit: 0,
        timestamp: { $gt: timestamp },
        $sort: { timestamp: 1 },
      };
      if (this._workItem.deletable && timestamp <= initialSyncStart) {
        // Initial Sync not completed
        // Archived items should not be in the local database.
        // It is needed to sync archived items anyway if they changed after initialSyncStart, as they could have been
        // archived recently and we might have an outdated, non-archived version of the same item in the database
        // that needs to be updated (deleted)
        query.$or = [{ archived: { $ne: true } }, { timestamp: { $gt: initialSyncStart } }];
      }

      // During the initial sync the per-item FTS/search-index writes are skipped in the work item;
      // the index is rebuilt once in bulk after the whole sync completes (FtsIndexFinalizer). The
      // flag is fixed for the whole run (timestamp is the run's starting point), so an incremental
      // sync keeps maintaining the index per item.
      const isInitialSync = timestamp <= initialSyncStart;

      let result: any;
      let progress = 0;
      let totalItemsToSync = 0;

      // Keyset (cursor) pagination state. Instead of a growing $skip - which forces MongoDB to walk
      // and discard every skipped document (O(skip) per page, O(n^2) over a full sync) - we advance
      // a cursor along the already-sorted `timestamp` and only ever read forward. `cursor` is the
      // exclusive lower bound for the next page; it starts at the incoming sync timestamp so the
      // very first page is byte-for-byte identical to the previous ($skip-based) behaviour.
      let cursor: Date = timestamp;
      // `skip` is only used as a fallback to page past a block of documents that all share the exact
      // same millisecond timestamp (see the saturation branch below); in normal data it stays 0.
      let skip = 0;
      let done = false;

      // Freeze the run to an upper timestamp bound captured at the start. Documents written while the
      // sync is running get a newer timestamp and would otherwise keep every page full forever (the
      // cursor only stops on a short page), so the loop would never finish on a busy collection.
      // Anything newer than this bound is left for the next sync cycle (its timestamp is greater than
      // this run's resume point, so nothing is lost).
      const runUpperBound = Date.now();

      if (!token.cancelled.get()) {
        // Initial $limit: 0 request: used once to obtain the total for the progress events.
        result = await this._service.find(this.patchParams(this.getParamsWithAuth(user, query)));
        resetServiceRepeats(token, this._name);

        totalItemsToSync = this.isPaginated(result) ? result.total : result.length;
        // Only a paginated probe yields a reliable total. A non-paginated service answers the
        // $limit: 0 probe with an empty array even when records exist, so we must not treat that as
        // "nothing to do" - enter the loop and let the first real page decide (matches the previous
        // behaviour, which defaulted total to 1 for non-paginated services).
        done = this.isPaginated(result) ? totalItemsToSync === 0 : false;
        this._eventService.dispatchEvent(new SyncProgressEvent(this._name, totalItemsToSync, progress));
      }

      while (!done && !token.cancelled.get()) {
        await this.sleep();

        // Page forward from the cursor. The base `timestamp: { $gt: cursor }` filter is rewritten
        // each round; the archived/$or business logic and the $sort carried over by the spread stay
        // untouched (the $or still compares against the fixed initialSyncStart, not the cursor).
        // $paginate: false skips the expensive per-page countDocuments (the total is known already).
        query = { ...query, timestamp: { $gt: cursor }, $limit: this.limit, $skip: skip, $paginate: false };

        // Once the cursor has moved past initialSyncStart every remaining document is newer than
        // initialSyncStart, so the $or's second clause is always true and the whole $or is
        // redundant. Dropping it lets MongoDB use the plain { regionId, timestamp } index instead of
        // planning around the disjunction. This is semantically identical to keeping the $or and
        // mirrors what AttachmentSyncService already does.
        if (query.$or && cursor.getTime() >= initialSyncStart.getTime()) {
          delete query.$or;
        }

        if (token.cancelled.get()) {
          break;
        }

        result = await this._service.find(this.patchParams(this.getParamsWithAuth(user, query)));
        resetServiceRepeats(token, this._name);

        const data = this.isPaginated(result) ? result.data : result;

        const syncState = await (this._workItem as IDatabaseSynchronizer<T>).sync(data, token, isInitialSync);
        await this._dispatcher.sync(this._name, syncState);
        const payload = [...syncState.created, ...syncState.deleted, ...syncState.updated];

        await resetSyncTimestamp({ payload, documentCount: result.documentCount });
        await this.afterSync(syncState);

        try {
          progress += payload.length;
          // `progress` counts PROCESSED documents and therefore legitimately overshoots the total:
          // the keyset cursor below resumes at (page max timestamp - 1ms), so the boundary
          // document(s) of every page are fetched and applied again (~1 duplicate per page, i.e.
          // ~270 extra for 13.5k audits at page size 50), and documents changed on the server
          // while the run is in progress are processed too although `totalItemsToSync` is a
          // snapshot from the initial $limit:0 probe. The stored data is exact (writes are keyed
          // by _id) - only the displayed counter would run past its total (e.g. 13840/13569),
          // which users read as a bug (#25074). Clamp the dispatched value only: the unclamped
          // `progress` must stay in the tracker logs and the cancellation error below, where the
          // overshoot is diagnostic (it shows how much boundary re-fetching happened). Skip the
          // clamp when no reliable total exists - non-paginated services answer the probe with an
          // empty array, so their total is 0 and clamping would pin the display to 0/0.
          const displayProgress = totalItemsToSync > 0 ? Math.min(progress, totalItemsToSync) : progress;
          this._eventService.dispatchEvent(new SyncProgressEvent(this._name, totalItemsToSync, displayProgress));
        } catch (e) {}
        this.limit = this.isPaginated(result) ? result.limit : this.limit;

        // The last item of the ascending-sorted page carries the page's max timestamp and drives the
        // cursor. Guard against results that are not a pageable array (a non-paginated service can
        // return a single object) or items without a usable timestamp: in those cases there is
        // nothing to page forward, so finish instead of dereferencing undefined.
        const lastItem = Array.isArray(data) && data.length ? data[data.length - 1] : undefined;
        const maxTimestamp = lastItem ? new Date(lastItem.timestamp).getTime() : NaN;

        if (!Array.isArray(data) || data.length < this.limit || Number.isNaN(maxTimestamp)) {
          // A short/empty page, a non-array result, or a missing timestamp: nothing left to page.
          done = true;
        } else {
          if (maxTimestamp >= runUpperBound) {
            // Reached documents written at/after this run started. Stop here so a steady stream of
            // concurrent writes can't keep the loop going indefinitely; anything newer is synced in
            // the next cycle (its timestamp is greater than this run's resume point).
            //
            // Note: stopping here deliberately skips the (maxTimestamp - 1ms) re-inclusion below.
            // Edge case: this is a FULL page, so if MORE documents share exactly maxTimestamp beyond
            // this page boundary, they are not synced now AND are skipped next run too, because the
            // resume cursor becomes maxTimestamp and the next sync queries timestamp > maxTimestamp
            // (strict). Such a document is only picked up once it changes again (new timestamp) or on
            // a full re-sync. Accepted as-is: it requires >= 2 documents with the identical
            // millisecond timestamp exactly at this boundary, it is no regression over the previous
            // $skip/count loop (which also resumed via a strict >), and the root cause is duplicate
            // timestamps, not the pagination.
            done = true;
          } else {
            // $gt on (maxTimestamp - 1ms) behaves like $gte on maxTimestamp (timestamps are
            // millisecond-granular), so documents sharing the page-boundary timestamp are re-fetched
            // next round and re-applied idempotently (writes are keyed by _id). This guarantees no item
            // straddling the page edge is skipped - the failure mode a plain $gt cursor would have.
            const nextCursor = new Date(maxTimestamp - 1);

            if (nextCursor.getTime() <= cursor.getTime()) {
              // Saturation: a full page whose documents all share a single timestamp. The cursor cannot
              // advance without skipping unseen siblings, so fall back to $skip to page past the block
              // until a newer timestamp appears, then resume the cursor.
              skip += data.length;
            } else {
              cursor = nextCursor;
              skip = 0;
            }
          }
        }
      }

      token.syncCompleted[this._name] = done;
      this._trackerService.debugSyncServiceSyncInternalDone(
        this._name,
        timestamp,
        await this._syncTimestampWorkItem.getSyncTimestamp(this._name),
        initialSyncStart,
        token.syncCompleted[this._name]
      );

      if (token.cancelled.get()) {
        delete token.serviceRepeats[this._name];
        throw {
          reason: 'cancelled',
          service: this._name,
          progress: `progress: ${progress}`,
          total: `total: ${totalItemsToSync}`,
          level: LogLevel.silent,
        };
      }
    } catch (error) {
      // Report failing services to Sentry with the service as context, the same way other parts of
      // the app do. Cancellations (LogLevel.silent) are expected and must not be reported.
      if (error?.level !== LogLevel.silent) {
        window.logger.captureErrorWithExtras(
          `SYNCSERVICE SYNC / ${this._name}`,
          error,
          new Map([['service', this._name]]),
          'error'
        );
      }
      console.log(error);
      this._eventService.dispatchEvent(new SyncProgressEvent(this._name, -1, -1, true));
      throw error;
    }
  }

  public async sync(
    token: ICancellationToken,
    user: any,
    resetServiceRepeats: (token: ICancellationToken, serviceName: string) => void
  ): Promise<any> {
    const timestamps = await this.loadSyncTimestamps();
    return this.syncInternal(
      token,
      user,
      timestamps.last,
      timestamps.start,
      resetServiceRepeats,
      async data => await this._syncTimestampWorkItem.setSyncTimestamp(this._name, data.payload, timestamps.start)
    );
  }

  public async reSync(
    token: ICancellationToken,
    user: any,
    userSync: any,
    resetServiceRepeats: (token: ICancellationToken, serviceName: string) => void
  ): Promise<any> {
    if (
      !userSync ||
      !userSync.status ||
      !userSync.status.length ||
      !userSync.status.some(value => value.name === this._name)
    ) {
      return;
    }

    const serviceStatus = userSync.status.find(value => value.name === this._name);

    const userClone = cloneDeep(user);
    userClone.authorization.regions = [...serviceStatus.regions];

    const initialSyncStart = (await this.loadSyncTimestamps()).start;
    await this.syncInternal(
      token,
      userClone,
      serviceStatus.timestamp,
      initialSyncStart,
      resetServiceRepeats,
      async data => {
        const syncMaxTimestamp = await this._syncTimestampWorkItem.getSyncTimestamp(this._name);
        const payloadMaxTimestamp = this._syncTimestampWorkItem.getLatestDate(data.payload);

        serviceStatus.timestamp = new Date(payloadMaxTimestamp || 0);

        if (serviceStatus.timestamp <= syncMaxTimestamp) {
          return;
        }

        await this._syncTimestampWorkItem.setSyncTimestamp(this._name, data.payload, initialSyncStart);
      }
    );

    userSync.status = userSync.status.filter(value => value.name !== this._name);
  }

  public async countDocuments(user: IUser): Promise<number> {
    const query: any = { $limit: 0, timestamp: { $gt: new Date(0) } };
    if (this._workItem.deletable) {
      query.archived = { $ne: true };
    }

    const result = await this._service.find(this.patchParams(this.getParamsWithAuth(user, query)));

    return result.total || result.length;
  }

  protected async onCreated(data: any): Promise<void> {
    if (this.platformSync.canBeSynced) {
      await (this._workItem as IWorkItem<T>).create(data);
    }
  }

  protected async onPatched(data: any): Promise<void> {
    // update database only when sync mode is on or when patch is for users
    if (this.platformSync.canBeSynced || this._name === UsersModelName || this._name === PatientAppUserModelName) {
      await (this._workItem as IDatabaseSynchronizer<T>).sync(data, {
        cancelled: new ObservableModel(false, false),
        promise: undefined,
        cancel: () => undefined,
        reset: () => undefined,
      });
    }

    if (this.ignoreState) {
      return;
    }
    await this._dispatcher.updateState(this._name, [data]);
  }

  protected async onRemoved(data: any): Promise<void> {
    if (this.platformSync.canBeSynced) {
      await (this._workItem as IWorkItem<T>).delete(data);
    }

    if (this.ignoreState) {
      return;
    }
    await this._dispatcher.removeFromState(this._name, data);
  }

  protected sleep(ms?: number) {
    return new Promise(resolve => this._ngZone.runOutsideAngular(() => setTimeout(resolve, ms || 0)));
  }

  protected getParamsWithAuth(user, query = {}): Params {
    const paramsWithAuth = { query: { ...query } };
    if (user && user.authorization) {
      paramsForServer({
        query: paramsWithAuth.query,
        syncData: this.createSyncData(user),
      });
    }

    return paramsWithAuth;
  }

  private createSyncData(user: any, additionalData?: IAdditionalSyncData): any {
    // log to find reason for undefined tenantId at server
    let syncData = {
      authorization: user.authorization,
      emailAPI:
        user.organization.alberta && user.organization.alberta.emailAPI ? user.organization.alberta.emailAPI : false,
      emailTo: user.organization.alberta && user.organization.alberta.emailTo ? user.organization.alberta.emailTo : [],
      returnDeliveryEmailAPI:
        user.organization.alberta && user.organization.alberta.returnDeliveryEmailAPI
          ? user.organization.alberta.returnDeliveryEmailAPI
          : false,
      returnDeliveryEmailTo:
        user.organization.alberta && user.organization.alberta.returnDeliveryEmailTo
          ? user.organization.alberta.returnDeliveryEmailTo
          : [],
      tenantId: user.organization.tenantId,
    };

    if (additionalData) {
      if (additionalData.additionalSyncInfo) {
        syncData = { ...syncData, ...additionalData.additionalSyncInfo };
      }
      if (additionalData.changes) {
        syncData['changes'] = additionalData.changes;
      }
    }

    return syncData;
  }

  protected isPaginated(paginated: any[] | Paginated<any>): paginated is Paginated<any> {
    return paginated && (paginated as Paginated<any>).data !== undefined;
  }

  private async loadSyncTimestamps(): Promise<{ start: Date; last: Date }> {
    const last = await this._syncTimestampWorkItem.getSyncTimestamp(this._name);
    let start = await this._syncTimestampWorkItem.getInitialSyncStart(this._name);
    if (start === null) {
      if (last.valueOf() === 0) {
        // initial sync
        start = new Date();
      } else {
        // Sync did run in the past, without initialSync timestamp getting set.
        // This happens in older versions of Alberta.
        // Setting initialSyncStart to the past.
        start = new Date(0);
      }
    }
    return { start, last };
  }
}
